import { FC, ReactNode } from 'react'
import { matchPath, Outlet, useLocation } from 'react-router-dom'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useIntl } from 'react-intl'

import Dashboard from 'layouts/Dashboard'
import NotificationsTabs from 'components/Notifications/NotificationsTabs'

interface NotificationsLayoutProps {
  // Master list rendered in the left column on desktop (notifications).
  navigationComponent?: ReactNode
  // Root path of the section: '/notifications' or '/discounts'.
  rootPath: string
  // Route patterns rendered as a centered white card on desktop (detail pages).
  singleColumnPaths?: string[]
}

// Layout for the Powiadomienia / Strefa rabatów section ("Panel Klienta 3.0").
// Notifications desktop: full-bleed split (list + detail).
// Discounts desktop: toggle pills on top, content below.
// Mobile: header with tabs on the root path, full-screen subpages elsewhere.
const NotificationsLayout: FC<NotificationsLayoutProps> = ({
  navigationComponent,
  rootPath,
  singleColumnPaths = []
}) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { pathname } = useLocation()
  const { formatMessage } = useIntl()

  const isSingleColumn = singleColumnPaths.some((pattern) => matchPath(pattern, pathname))
  const isNotificationsSplit = rootPath === '/notifications' && !!navigationComponent

  const title =
    rootPath === '/discounts'
      ? formatMessage({ id: 'discounts.zone', defaultMessage: 'Strefa rabatów' })
      : formatMessage({ id: 'notificationsPage.notifications', defaultMessage: 'Powiadomienia' })

  if (isDesktop && isNotificationsSplit) {
    return (
      <Dashboard noHeader disableGutters>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            minHeight: 'calc(100vh - 56px)',
            backgroundColor: theme.r4r.greyBg
          }}>
          <Box
            sx={{
              width: { md: '360px', lg: '440px' },
              flexShrink: 0,
              backgroundColor: '#fff',
              borderRight: `1px solid ${theme.r4r.greyLight}`,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
            {navigationComponent}
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: theme.r4r.greyBg
            }}>
            <Outlet />
          </Box>
        </Box>
      </Dashboard>
    )
  }

  if (isDesktop) {
    return (
      <Dashboard noHeader disableGutters>
        <Box sx={{ backgroundColor: theme.r4r.greyBg, minHeight: '100vh' }}>
          <Box sx={{ maxWidth: '1220px', mx: 'auto', px: '40px', pb: '64px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                pt: '28px',
                pb: '20px',
                borderBottom: `1px solid ${theme.r4r.greyLight}`
              }}>
              <NotificationsTabs />
            </Box>
            <Box sx={{ pt: '24px' }}>
              {isSingleColumn ? (
                <Box
                  sx={{
                    maxWidth: '760px',
                    mx: 'auto',
                    backgroundColor: '#fff',
                    boxShadow: theme.r4r.cardShadow,
                    p: '28px'
                  }}>
                  <Outlet />
                </Box>
              ) : (
                <Outlet />
              )}
            </Box>
          </Box>
        </Box>
      </Dashboard>
    )
  }

  if (pathname === rootPath) {
    return (
      <Dashboard noHeader>
        <Box sx={{ backgroundColor: theme.r4r.greyBg, minHeight: '100vh', pb: '24px' }}>
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 700,
              color: theme.r4r.navy,
              textAlign: 'center',
              pt: '18px',
              pb: '14px'
            }}>
            {title}
          </Typography>
          <Box sx={{ px: '16px' }}>
            <NotificationsTabs />
          </Box>
          <Box sx={{ px: rootPath === '/notifications' ? 0 : '16px', pt: '16px' }}>
            <Outlet />
          </Box>
        </Box>
      </Dashboard>
    )
  }

  return <Outlet />
}

export default NotificationsLayout
